/**
 * Canonical list of payment methods supported across Forest.
 *
 * Which methods an operator allows for plans is driven by the
 * `planPaymentMethods` config key — see `apps/database/schemas/journey.schema.json`.
 */

export type PaymentMethod = 'card' | 'sepa' | 'multibanco' | 'mb_way' | 'bacs';

/**
 * Minimum days between today and a method's first scheduled payment date.
 * Methods with no entry have no lead-time restriction. Mirrors the connect-api
 * SCHEDULING_MIN_LEAD_DAYS map — see payment_service.py.
 */
export const SCHEDULING_MIN_LEAD_DAYS: Partial<Record<PaymentMethod, number>> = {
  bacs: 7,
};

export interface MethodDateAvailability {
  available: boolean;
  /** First date (local midnight) the method starts qualifying; null once available. */
  availableFrom: Date | null;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * The UTC calendar day `d` falls on, represented as a local-midnight `Date`
 * so it compares directly against a `startOfDay`-normalized local date.
 * connect-api's lead-time check anchors "today" to `datetime.now(UTC).date()`
 * (`_validate_schedule_lead_time`) while a picked date is always the
 * customer's local calendar day (it's serialized via local getters —
 * `toScheduleDate`); anchoring "today" to UTC here keeps this check and the
 * server's in agreement regardless of the browser's timezone.
 */
function startOfUtcDayAsLocal(d: Date): Date {
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Whether each method's lead-time floor (if any) is cleared by `date`. Methods
 * with no `SCHEDULING_MIN_LEAD_DAYS` entry are always available.
 */
export function methodDateAvailability(
  methods: readonly PaymentMethod[],
  date: Date,
  today: Date = new Date()
): Partial<Record<PaymentMethod, MethodDateAvailability>> {
  const dateOnly = startOfDay(date);
  const todayOnly = startOfUtcDayAsLocal(today);
  const result: Partial<Record<PaymentMethod, MethodDateAvailability>> = {};
  for (const method of methods) {
    const leadDays = SCHEDULING_MIN_LEAD_DAYS[method];
    if (leadDays === undefined) {
      result[method] = { available: true, availableFrom: null };
      continue;
    }
    const minAllowed = new Date(todayOnly);
    minAllowed.setDate(minAllowed.getDate() + leadDays);
    result[method] = dateOnly >= minAllowed ? { available: true, availableFrom: null } : { available: false, availableFrom: minAllowed };
  }
  return result;
}
